import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { fetchPlans } from '../../shared/api/endpoints';
import { PlanCards, planName, formatUsd } from './PlanCards';
import type { PlanDto } from '@foco/contracts';

/**
 * Pricing (section 13.2).
 *
 * The comparison table renders the same numbers the cards do, because the
 * customer's policy only works if it is legible: the one-off plan is the most
 * expensive per request on purpose, and hiding that would make the pricing feel
 * arbitrary rather than deliberate.
 */
export function PricingPage(): React.ReactNode {
  const { t } = useTranslation();

  const plans = useQuery({
    queryKey: ['plans'],
    queryFn: ({ signal }) => fetchPlans({ signal }),
    staleTime: 5 * 60 * 1000,
  });

  return (
    <div className="mx-auto max-w-6xl px-4 py-12">
      <h1 className="text-3xl font-bold tracking-tight">{t('pricing.title')}</h1>
      <p className="mt-2 max-w-2xl text-[var(--color-muted-fg)]">{t('pricing.subtitle')}</p>

      {plans.isLoading && (
        <p className="mt-8 text-sm text-[var(--color-muted-fg)]">{t('common.loading')}</p>
      )}
      {plans.isError && (
        <p className="mt-8 text-sm text-[var(--color-muted-fg)]">
          {t('errors.generic')}
        </p>
      )}
      {plans.data && <PlanCards plans={plans.data} />}

      {plans.data && <Comparison plans={plans.data} />}

      <section className="mt-12 max-w-3xl">
        <h2 className="text-xl font-bold">{t('pricing.faqTitle')}</h2>
        <dl className="mt-4 space-y-4">
          {/* These entries are objects with `question` and `answer`, so both are
              addressed explicitly — asking i18next for the parent key returns an
              object and React renders it as a broken `[object Object]` node. */}
          {(['faqCancel', 'faqChange', 'faqQuota', 'faqInvoice', 'faqCards'] as const).map((key) => (
            <div key={key}>
              <dt className="font-medium">{t(`pricing.${key}.question`)}</dt>
              <dd className="mt-1 text-sm text-[var(--color-muted-fg)]">
                {t(`pricing.${key}.answer`)}
              </dd>
            </div>
          ))}
        </dl>
      </section>

      <p className="mt-8 text-xs text-[var(--color-muted-fg)]">{t('pricing.currencyNote')}</p>
    </div>
  );
}

/** The per-request comparison that makes the pricing policy legible. */
function Comparison({ plans }: { plans: readonly PlanDto[] }): React.ReactNode {
  const { t, i18n } = useTranslation();

  return (
    <section className="mt-12">
      <h2 className="text-xl font-bold">{t('pricing.comparisonTitle')}</h2>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[32rem] border-collapse text-sm">
          <thead>
            <tr className="border-b border-[var(--color-border)] text-left">
              <th scope="col" className="py-2 pr-4 font-medium">{t('pricing.feature')}</th>
              {plans.map((plan) => (
                <th scope="col" key={plan.id} className="py-2 pr-4 font-medium">
                  {planName(t, plan.id)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="text-[var(--color-muted-fg)]">
            <tr className="border-b border-[var(--color-border)]">
              <th scope="row" className="py-2 pr-4 text-left font-normal">{t('pricing.oneTime')}</th>
              {plans.map((plan) => (
                <td key={plan.id} className="py-2 pr-4">
                  {formatUsd(plan.priceUsd, i18n.language)}
                </td>
              ))}
            </tr>
            <tr className="border-b border-[var(--color-border)]">
              <th scope="row" className="py-2 pr-4 text-left font-normal">{t('pricing.requests')}</th>
              {plans.map((plan) => (
                <td key={plan.id} className="py-2 pr-4">
                  {plan.requestsLimit > 0
                    ? t('plans.requests', { count: plan.requestsLimit })
                    : t('plans.unlimitedNo')}
                </td>
              ))}
            </tr>
            <tr className="border-b border-[var(--color-border)]">
              {/* The column header is the label; `pricing.perRequest` itself
                  carries a `{{price}}` placeholder and is for the value, so the
                  header uses its own key rather than rendering the raw braces. */}
              <th scope="row" className="py-2 pr-4 text-left font-normal">
                {t('pricing.perRequestLabel')}
              </th>
              {plans.map((plan) => (
                <td key={plan.id} className="py-2 pr-4 font-mono text-xs">
                  {plan.priceUsd > 0 && plan.requestsLimit > 0
                    ? formatUsd(plan.priceUsd / plan.requestsLimit, i18n.language, 4)
                    : '—'}
                </td>
              ))}
            </tr>
            <tr className="border-b border-[var(--color-border)]">
              <th scope="row" className="py-2 pr-4 text-left font-normal">{t('pricing.seats')}</th>
              {plans.map((plan) => (
                <td key={plan.id} className="py-2 pr-4">
                  {plan.seatsIncluded}
                </td>
              ))}
            </tr>
            <tr>
              <th scope="row" className="py-2 pr-4 text-left font-normal">{t('pricing.api')}</th>
              {plans.map((plan) => (
                <td key={plan.id} className="py-2 pr-4">
                  {plan.apiAccess ? t('pricing.yes') : t('pricing.no')}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}
